import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Shield, ArrowRight, Loader2 } from "lucide-react";
import vexisLogo from "@assets/vexis-logo-2026.jpg";

const MUTED = "#94a3b8";
const INDIGO = "#6366f1";
const BORDER = "rgba(99,102,241,0.18)";

export default function AdminLogin() {
  const { toast } = useToast();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = await login(formData.email, formData.password, "ADMIN");
      toast({ title: "Welcome back!", description: `Signed in as ${user.fullName || user.email}` });
    } catch (error: any) {
      toast({ title: "Login Failed", description: error.message || "Invalid credentials", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: "#05070a" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link href="/">
            <img src={vexisLogo} alt="VEXIS" className="h-12 w-auto mx-auto cursor-pointer" />
          </Link>
          <h2 className="text-2xl font-bold text-white mt-4">Internal Admin</h2>
          <p className="text-sm mt-2" style={{ color: MUTED }}>Vexis staff access only</p>
        </div>

        <div className="rounded-2xl p-8" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${BORDER}` }}>
          <div className="flex items-center justify-center gap-2 mb-6 pb-5" style={{ borderBottom: `1px solid ${BORDER}` }}>
            <Shield className="w-5 h-5" style={{ color: INDIGO }} />
            <span className="font-semibold text-white">Admin Login</span>
          </div>

          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white text-sm">Email Address</Label>
              <Input id="email" type="email" placeholder="admin@vexistalentagency.com"
                value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                required data-testid="input-email"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white text-sm">Password</Label>
              <Input id="password" type="password" placeholder="Password"
                value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })}
                required data-testid="input-password"
                className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 focus:border-indigo-500" />
            </div>
            <Button type="submit" className="w-full h-12 font-semibold text-white"
              style={{ backgroundColor: INDIGO }} disabled={isLoading} data-testid="button-login">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {isLoading ? "Signing in..." : "Sign In"}
              {!isLoading && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <Link href="/" className="text-sm transition-colors" style={{ color: MUTED }}>
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
